import type { InputChipProps } from '../chip';
import type { SelectOption } from '../select';
import type { ClipboardEvent, KeyboardEvent, MouseEvent, ReactElement, RefObject } from 'react';

import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { useForwardRef } from '../../hooks/use-forward-ref';
import { classNames } from '../../utils/class-names';
import { SelectMenu } from '../select/select-menu';

import styles from './chips-input.module.css';

export type ChipsInputProps = {
  separators?: string[];
  disabled?: boolean;
  className?: {
    input?: string;
    chipsInput?: string;
  };
  children?: ReactElement<InputChipProps> | ReactElement<InputChipProps>[];
  options?: SelectOption[];
  values?: string[];
  optionsIdProperty?: string;
  getOptionsLabel?: (option: SelectOption) => string;
  isSearchable?: boolean;
  forceMenuWidth?: boolean;
  onChipsAdded: (values: string[]) => void;
  inputRef?: RefObject<HTMLInputElement>;
  placeholder?: string;
};

/**
 * @param {ReactElement<ChipProps> | ReactElement<ChipProps>[]} [children] Chips to be displayed (use <Chip /> component).
 * @param {string[]} [separators] - List of keys that creates a new chip. See the [DOM Level 3 Events spec](https://www.w3.org/TR/uievents-key/#named-key-attribute-values)
 * @param {boolean} [disabled] - Whether the component is disabled or not.
 * @param {Object} [className] - Object to define className for the input and the whole chipsInput component.
 * @param {string[]} [values] - The already selected values to be filtered from options.
 * @param {SelectOption[]} [options] - An array of options to be displayed in the dropdown.
 * @param {string} [optionsIdProperty] - Specify the property to be used as the id of the options objects.
 * @default 'id'
 * @param {(option: SelectOption) => string} [getOptionsLabel] - A callback function to get the label from option object, defaults to [option.label || option[optionsIdProperty]].
 * @default undefined
 * @param {boolean} [isSearchable] - A flag to display dropdown.
 * @param {boolean} [forceMenuWidth] - A flag to match the width of the dropdown to the input
 * @default true
 * @param {Function} onChipsAdded - Callback function invoked when new chips are added.
 * @param {string} [placeholder] - Placeholder text for the input.
 */
export const ChipsInput = forwardRef<HTMLInputElement, ChipsInputProps>(
  (
    {
      separators = ['Enter'],
      disabled,
      className,
      children,
      options,
      values,
      getOptionsLabel,
      optionsIdProperty = 'id',
      isSearchable,
      forceMenuWidth = true,
      onChipsAdded,
      placeholder,
    },
    ref
  ) => {
    getOptionsLabel = getOptionsLabel || ((option: SelectOption) => option?.label || option?.[optionsIdProperty]);

    const [inputValue, setValue] = useState('');
    const containerRef = useRef(null);
    const inputRef = useForwardRef(ref);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const mappedOptions = useMemo(() => {
      if (!options) return [];

      return options
        .filter((option) => values && !values.includes(option[optionsIdProperty]))
        .map((option) => ({ id: option, label: option }));
    }, [options, values, optionsIdProperty]);
    const [displayOptions, setDisplayOptions] = useState(mappedOptions);

    const addChip = (value: string) => {
      onChipsAdded([value]);
      setValue('');
    };

    const handleOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (separators.includes(event.key) && inputValue.trim().length > 0) {
        event.preventDefault();
        addChip(inputValue);
      }
    };

    const handleOnContainerClick = (event: MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        inputRef.current?.focus();
      }
    };

    const handleOnFocus = () => {
      setIsMenuOpen(true);
    };

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pastedText = event.clipboardData.getData('text/plain');

      const items = pastedText.split(',').map((item) => item.trim());
      onChipsAdded(items);
    };

    useEffect(
      function filterOptions() {
        if (!isSearchable) return;

        const searchText = inputValue.toLowerCase().trim();

        const filteredOptions = mappedOptions.filter(({ label }) => {
          return label?.toLowerCase().includes(searchText);
        });
        setDisplayOptions(filteredOptions);
      },
      [mappedOptions, inputValue, isSearchable]
    );

    return (
      <div
        data-testid="chips-input"
        className={classNames(styles.chipsInput, className?.chipsInput, disabled && styles.disabled)}
        onClick={handleOnContainerClick}
        ref={containerRef}
      >
        {children}
        <input
          data-testid="chips-text-input"
          className={classNames(styles.input, className?.input)}
          type="text"
          disabled={disabled}
          onKeyDown={handleOnKeyDown}
          value={inputValue}
          onChange={(event) => setValue(event.target.value)}
          ref={inputRef}
          onFocus={handleOnFocus}
          onPaste={handlePaste}
          placeholder={disabled ? '' : placeholder}
        />
        {isSearchable && isMenuOpen && (
          <SelectMenu
            testId="chips-input-select-menu"
            className={styles.menu}
            isOpen={isMenuOpen}
            onClose={() => {
              setIsMenuOpen(false);
            }}
            closeOnChange={false}
            onChange={(option: SelectOption) => {
              addChip(getOptionsLabel(option));
              inputRef.current?.focus();
            }}
            options={displayOptions}
            optionsIdProperty={optionsIdProperty}
            getOptionsLabel={getOptionsLabel}
            isSearchable={false}
            forceMenuWidth={forceMenuWidth}
            ref={containerRef}
          />
        )}
      </div>
    );
  }
);
